import { Environment } from 'dashboard/models/environment';
import { InquiryTemplate, InquiryTemplateFilterOption } from 'dashboard/models/inquiry-template';
import { Organization } from 'dashboard/models/organization';
import { OrganizationPlan } from 'dashboard/models/organization-plan';
import { OrganizationPlanMigration } from 'dashboard/models/organization-plan-migration';
import { Template, TemplateFilterOption } from 'dashboard/models/template';
import { Theme } from 'dashboard/models/theme';
import {
  VerificationTemplate,
  VerificationTemplateFilterOption,
} from 'dashboard/models/verification-template';
import Action from 'lib/interfaces/Action';

export type OrganizationActions =
  | Action<
      OrganizationActionType.SetInquiryTemplatesForOrganization,
      SetInquiryTemplatesForOrganizationPayload
    >
  | Action<
      OrganizationActionType.SetInquiryTemplateFilterOptions,
      SetInquiryTemplateFilterOptionsPayload
    >
  | Action<OrganizationActionType.SetTemplates, SetTemplatesPayload>
  | Action<OrganizationActionType.SetTemplateFilterOptions, SetTemplateFilterOptionsPayload>
  | Action<OrganizationActionType.SetVerificationTemplates, SetVerificationTemplatesPayload>
  | Action<
      OrganizationActionType.SetVerificationTemplateFilterOptions,
      SetVerificationTemplateFilterOptionsPayload
    >
  | Action<OrganizationActionType.SetTemplate, SetTemplatePayload>
  | Action<OrganizationActionType.SetEnvironments, SetEnvironmentsPayload>
  | Action<OrganizationActionType.SetEnvironment, SetEnvironmentPayload>
  | Action<OrganizationActionType.SetOrganization, SetOrganizationPayload>
  | Action<OrganizationActionType.SetCurrentOrganizationPlan, SetCurrentOrganizationPlanPayload>
  | Action<
      OrganizationActionType.SetPendingOrganizationPlanMigration,
      SetPendingOrganizationPlanMigrationPayload
    >
  | Action<OrganizationActionType.SetThemes, SetThemesPayload>
  | Action<OrganizationActionType.SetTheme, SetThemePayload>;

export enum OrganizationActionType {
  SetInquiryTemplatesForOrganization = 'organization/SET_INQUIRY_TEMPLATES_FOR_ORGANIZATION',
  SetInquiryTemplateFilterOptions = 'organization/SET_INQUIRY_TEMPLATE_FILTER_OPTIONS',
  SetTemplates = 'organization/SET_TEMPLATES',
  SetTemplateFilterOptions = 'organization/SET_TEMPLATE_FILTER_OPTIONS',
  SetTemplate = 'organization/SET_TEMPLATE',
  SetVerificationTemplates = 'organization/SET_VERIFICATION_TEMPLATES_FOR_ORGANIZATION',
  SetVerificationTemplateFilterOptions = 'organization/SET_VERIFICATION_TEMPLATE_FILTER_OPTIONS',
  SetEnvironments = 'organization/SET_ENVIRONMENTS',
  SetEnvironment = 'organization/SET_ENVIRONMENT',
  SetOrganization = 'organization/SET_ORGANIZATION',
  SetCurrentOrganizationPlan = 'organization/SET_CURRENT_ORGANIZATION_PLAN',
  SetPendingOrganizationPlanMigration = 'organization/SET_PENDING_ORGANIZATION_PLAN_MIGRATION',
  SetTheme = 'organization/SET_THEME',
  SetThemes = 'organization/SET_THEMES',
}

interface SetInquiryTemplatesForOrganizationPayload {
  inquiryTemplates: InquiryTemplate[];
}

export function setInquiryTemplatesForOrganization(
  inquiryTemplates: InquiryTemplate[],
): Action<
  OrganizationActionType.SetInquiryTemplatesForOrganization,
  SetInquiryTemplatesForOrganizationPayload
> {
  return {
    payload: {
      inquiryTemplates,
    },
    type: OrganizationActionType.SetInquiryTemplatesForOrganization,
  };
}

interface SetInquiryTemplateFilterOptionsPayload {
  inquiryTemplateFilterOptions: InquiryTemplateFilterOption[];
}

export function setInquiryTemplateFilterOptionsForOrganization(
  inquiryTemplateFilterOptions: InquiryTemplateFilterOption[],
): Action<
  OrganizationActionType.SetInquiryTemplateFilterOptions,
  SetInquiryTemplateFilterOptionsPayload
> {
  return {
    payload: {
      inquiryTemplateFilterOptions,
    },
    type: OrganizationActionType.SetInquiryTemplateFilterOptions,
  };
}

interface SetTemplatesPayload {
  templates: Template[];
}

export function setTemplatesForOrganization(
  templates: Template[],
): Action<OrganizationActionType.SetTemplates, SetTemplatesPayload> {
  return {
    payload: {
      templates,
    },
    type: OrganizationActionType.SetTemplates,
  };
}

interface SetTemplateFilterOptionsPayload {
  templateFilterOptions: TemplateFilterOption[];
}

export function setTemplateFilterOptionsForOrganization(
  templateFilterOptions: TemplateFilterOption[],
): Action<OrganizationActionType.SetTemplateFilterOptions, SetTemplateFilterOptionsPayload> {
  return {
    payload: {
      templateFilterOptions,
    },
    type: OrganizationActionType.SetTemplateFilterOptions,
  };
}

interface SetVerificationTemplatesPayload {
  verificationTemplates: VerificationTemplate[];
}

export function setVerificationTemplatesForOrganization(
  verificationTemplates: VerificationTemplate[],
): Action<OrganizationActionType.SetVerificationTemplates, SetVerificationTemplatesPayload> {
  return {
    payload: {
      verificationTemplates,
    },
    type: OrganizationActionType.SetVerificationTemplates,
  };
}

interface SetVerificationTemplateFilterOptionsPayload {
  verificationTemplateFilterOptions: VerificationTemplateFilterOption[];
}

export function setVerificationTemplateFilterOptionsForOrganization(
  verificationTemplateFilterOptions: VerificationTemplateFilterOption[],
): Action<
  OrganizationActionType.SetVerificationTemplateFilterOptions,
  SetVerificationTemplateFilterOptionsPayload
> {
  return {
    payload: {
      verificationTemplateFilterOptions,
    },
    type: OrganizationActionType.SetVerificationTemplateFilterOptions,
  };
}

interface SetTemplatePayload {
  template: Template;
}

export function setTemplate(
  template: Template,
): Action<OrganizationActionType.SetTemplate, SetTemplatePayload> {
  return {
    payload: {
      template,
    },
    type: OrganizationActionType.SetTemplate,
  };
}

interface SetEnvironmentsPayload {
  environments: Environment[];
}

export function setEnvironments(
  environments: Environment[],
): Action<OrganizationActionType.SetEnvironments, SetEnvironmentsPayload> {
  return {
    payload: {
      environments,
    },
    type: OrganizationActionType.SetEnvironments,
  };
}

interface SetEnvironmentPayload {
  environment: Environment;
}

export function setEnvironment(
  environment: Environment,
): Action<OrganizationActionType.SetEnvironment, SetEnvironmentPayload> {
  return {
    payload: {
      environment,
    },
    type: OrganizationActionType.SetEnvironment,
  };
}

interface SetOrganizationPayload {
  organization: Organization;
}

export function setOrganization(
  organization: Organization,
): Action<OrganizationActionType.SetOrganization, SetOrganizationPayload> {
  return {
    payload: {
      organization,
    },
    type: OrganizationActionType.SetOrganization,
  };
}

interface SetCurrentOrganizationPlanPayload {
  currentOrganizationPlan: OrganizationPlan;
}

interface SetPendingOrganizationPlanMigrationPayload {
  pendingOrganizationPlanMigration: OrganizationPlanMigration;
}

export function setCurrentOrganizationPlan(
  currentOrganizationPlan: OrganizationPlan,
): Action<OrganizationActionType.SetCurrentOrganizationPlan, SetCurrentOrganizationPlanPayload> {
  return {
    payload: {
      currentOrganizationPlan,
    },
    type: OrganizationActionType.SetCurrentOrganizationPlan,
  };
}

export function setPendingOrganizationPlanMigration(
  pendingOrganizationPlanMigration: OrganizationPlanMigration,
): Action<
  OrganizationActionType.SetPendingOrganizationPlanMigration,
  SetPendingOrganizationPlanMigrationPayload
> {
  return {
    payload: {
      pendingOrganizationPlanMigration,
    },
    type: OrganizationActionType.SetPendingOrganizationPlanMigration,
  };
}

interface SetThemesPayload {
  themes: Theme[];
}

export function setThemes(
  themes: Theme[],
): Action<OrganizationActionType.SetThemes, SetThemesPayload> {
  return {
    payload: {
      themes,
    },
    type: OrganizationActionType.SetThemes,
  };
}

interface SetThemePayload {
  theme: Theme;
}

export function setTheme(theme: Theme): Action<OrganizationActionType.SetTheme, SetThemePayload> {
  return {
    payload: {
      theme,
    },
    type: OrganizationActionType.SetTheme,
  };
}
